import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from "@angular/core";
import {MenuExtraGroupData, MenuExtraProductData, MenuItemData} from "../../../../menu/menu-entity.model";
import {Subscription} from "rxjs";
import {SessionModel} from "../../../../orchestration/session.model";
import {NavigationService} from "../../../../orchestration/navigation.service";
import {NotificationService} from "../../../../notification/notification.service";
import {ResponsiveService} from "../../../../orchestration/responsive.service";
import {
    OrderItemOptionControlComponent,
    OrderItemOptionLayout
} from "../order-item-option-control/order-item-option-control.component";
import {SelectorType} from "../../../../inputs/selector-control/selector";
import {MenuModel} from "../../../../menu/menu.model";
import {OrderModel} from "../../../order.model";
import {OrderingContextModel} from "../../../../ordering-context/ordering-context.model";
import {OrchestrationEvents} from "../../../../orchestration/orchestration-events";
import {OrderEvents} from "../../../order-events";
import {SessionEvents} from "../../../../orchestration/session-events";
import {OrderCompositeItem, OrderExtraItem, OrderItem, OrderType} from "../../../order";
import {SellableSizesData} from "../../../../menu/sellable-sizes-search.model";

@Component({
    selector: "woa-order-item-extras-edit-panel",
    templateUrl: "./order-item-extras-edit-panel.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderItemExtrasEditPanelComponent implements OnInit, OnDestroy {
    @Input() mainOrderItem: OrderItem;
    @Input() menuItem: MenuItemData;
    @Input() compositeItem: boolean = false;
    @Input() minimalMode: boolean = false;
    @Input() promotedExtraGroup: MenuExtraGroupData;
    @Input() showOnlyCombinedExtras: boolean;
    @Input() originalOrderItem: OrderItem;
    @Input() allowInitialState: boolean = false;
    @Output() onChange: EventEmitter<MenuExtraGroupData> = new EventEmitter();

    MenuModel = MenuModel;
    SelectorType = SelectorType;
    OrderItemOptionLayout = OrderItemOptionLayout;
    SessionModel = SessionModel;
    OrchestrationEvents = OrchestrationEvents;
    OrderEvents = OrderEvents;

    individualExtraGroups: MenuExtraGroupData[] = [];
    combinedExtraGroups: MenuExtraGroupData[] = [];
    availableExtraGroupCount: number = 0;
    availableExtraProducts: Map<number, MenuExtraProductData[]> = new Map();
    extraGroupHasImages: Map<number, boolean> = new Map();
    alignAllSingleSelectExtrasForImages: boolean = false;
    singleSelectExtrasWithSize: Map<number, Map<number, string>> = new Map();
    multiSelectExtraGroupIsAlignedForImages: Map<number, boolean> = new Map();
    multiSelectExtraGroupIsCompacted: Map<number, boolean> = new Map();
    extraExpanded: Map<number, boolean> = new Map();
    preOrderedExtras: OrderExtraItem[];
    initialQtyOrderedBySellableId: { [sellableId: number]: number } = {};

    private _orderItem: OrderItem;
    private subscriptions: Subscription = new Subscription();
    private assigningCombinedExtras: boolean = false;

    private readonly alignMultiSelectExtrasBetweenGroups: boolean = false;

    constructor(public sessionModel: SessionModel,
                public orderingContextModel: OrderingContextModel,
                public menuModel: MenuModel,
                private orderModel: OrderModel,
                private navigationService: NavigationService,
                private notificationService: NotificationService,
                private changeDetectorRef: ChangeDetectorRef,
                public responsiveService: ResponsiveService) {
    }

    ngOnInit(): void {
        this.subscriptions.add(OrderEvents.orderChanged$.subscribe(() => {
            this.loadAvailableExtras();
            setTimeout(() => this.changeDetectorRef.detectChanges());
        }));
        this.subscriptions.add(SessionEvents.currentApp$.subscribe(() => {
            this.loadAvailableExtras();
            setTimeout(() => this.changeDetectorRef.detectChanges());
        }));

        if (this.allowInitialState) {
            this.orderItem.accumulateSellableQtyOrdered(this.orderItem.quantity, this.initialQtyOrderedBySellableId);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get orderItem(): OrderItem {
        return this._orderItem;
    }

    @Input()
    set orderItem(orderItem: OrderItem) {
        this._orderItem = orderItem;
        this.preOrderedExtras = orderItem.orderedExtras;
        this.loadAvailableExtras();
    }

    private loadAvailableExtras(): void {
        const prevAvailableExtraProducts: Map<number, MenuExtraProductData[]> = this.availableExtraProducts;
        const additionalDefaultableExtraProducts: MenuExtraProductData[] = [];

        this.combinedExtraGroups = [];
        this.availableExtraGroupCount = 0;
        this.availableExtraProducts = new Map();
        this.extraGroupHasImages.clear();
        this.singleSelectExtrasWithSize.clear();
        this.singleSelectExtrasWithSize.clear();
        this.alignAllSingleSelectExtrasForImages = false;
        this.multiSelectExtraGroupIsAlignedForImages.clear();
        this.multiSelectExtraGroupIsCompacted.clear();

        const firstTierExtraGroups: MenuExtraGroupData[] = [];
        const secondTierExtraGroups: MenuExtraGroupData[] = [];
        // noinspection JSMismatchedCollectionQueryUpdate
        let extraGroupTier: MenuExtraGroupData[];

        let anyNormalMultiSelectExtraGroupHasImages: boolean = false;
        let anyCompactMultiSelectExtraGroupHasImages: boolean = false;

        let itemsRemoved: boolean = false;

        let lastParentExtraGroupId: number = 0;
        let skippingParentGroup: boolean = false;

        this.orderItem.availableExtraGroups.forEach((extraGroup: MenuExtraGroupData) => {
            const parentExtraGroupId: number = this.menuModel.getParentExtraGroupId(extraGroup, this.orderItem.sellable);
            const isParentGroup: boolean = !parentExtraGroupId;
            const isChildGroup: boolean = parentExtraGroupId === lastParentExtraGroupId;
            if (isParentGroup) {
                lastParentExtraGroupId = extraGroup.id;
            }

            // Extra groups are skipped completely if:
            // - They are associated with a parent group which is being skipped; or
            // - The group is not visible (zero limit, single default option); or
            // - We are only showing combined extras (popup); or
            // - This is a promoted extra popup (for a different group).
            if ((skippingParentGroup && isChildGroup && extraGroup !== this.promotedExtraGroup) ||
                !this.menuModel.extraGroupIsVisible(extraGroup) ||
                (this.showOnlyCombinedExtras && !isChildGroup && (this.menuModel.extraGroupIsSingleSelect(extraGroup) || !this.menuModel.extraGroupIsOptional(extraGroup))) ||
                (this.promotedExtraGroup && !isChildGroup && extraGroup !== this.promotedExtraGroup)) {
                if (isParentGroup) {
                    skippingParentGroup = true;
                }
                return;
            }
            skippingParentGroup = false;

            const prevAvailableExtraProductsForGroup: MenuExtraProductData[] = prevAvailableExtraProducts.get(extraGroup.id) || [];

            // Extra groups are also skipped if they are conditionally unavailable. In which case any selected extras for the group are also removed.
            if (!this.orderItem.isExtraGroupConditionallyAvailable(extraGroup)) {
                prevAvailableExtraProductsForGroup.forEach((prevProduct: MenuExtraProductData) => {
                    if (this.orderItem.removeOrderExtraProduct(extraGroup, prevProduct)) {
                        itemsRemoved = true;
                    }
                });
                return;
            }

            let groupShowsExtraQuantities: boolean = false;

            // Selected vs unselected lists are used for managing order of multi-select extras.
            let selectedExtraProducts: MenuExtraProductData[] = [];
            let unselectedExtraProducts: MenuExtraProductData[] = [];

            let excludedSellableIds: Map<number, boolean> = new Map();
            let sellableIdsWithSize: Map<number, string> = new Map();

            // Find the available products for the extra group
            extraGroup.extraProducts.forEach((extraProduct: MenuExtraProductData) => {
                if (excludedSellableIds.get(extraProduct.productLine.sellableId)) {
                    return;
                }
                if (!this.orderItem.isExtraProductAvailable(extraProduct, this.menuItem)) {
                    return;
                }
                if (extraProduct.menuItem && !this.menuModel.extraGroupIsSingleSelect(extraGroup)) {
                    if (this.menuModel.getStandardItemImage(extraProduct.menuItem)) {
                        this.extraGroupHasImages.set(extraGroup.id, true);
                    }
                    if (this.sessionModel.currentApp.compactExtrasThreshold && !groupShowsExtraQuantities && this.showExtraQuantity(extraGroup, extraProduct)) {
                        groupShowsExtraQuantities = true;
                    }
                }

                if (this.menuModel.extraGroupIsSingleSelect(extraGroup)) {

                    // For single-select extras in size groups, only one option is displayed at a time.  We need to work out here which one to display.
                    // This may also require adding the extra product to the order if it's the only option.
                    let sizes: SellableSizesData[] = this.menuModel.findExtraSizes(extraProduct.productLine.sellableId, extraGroup);
                    if (sizes && sizes.length) {
                        let extraSellableIdToAdd: number;

                        // Try to find if any of the items are already added to the order item
                        let selectedSizeExtra: SellableSizesData = sizes.find((sellableSizes: SellableSizesData) => {
                            let extraProduct: MenuExtraProductData = extraGroup.extraProducts.find((extraProduct: MenuExtraProductData) => extraProduct.productLine.sellableId === sellableSizes.sellableId);
                            return extraProduct && this.orderItem.orderedExtras.find((extraItem: OrderExtraItem) => extraItem.extraGroup.id === extraGroup.id &&
                                extraItem.extraProduct.productLine.sellableId === extraProduct.productLine.sellableId) != undefined;
                        });
                        if (selectedSizeExtra) {
                            extraSellableIdToAdd = selectedSizeExtra.sellableId;
                        } else {

                            // Use the standard size sellable by default...
                            extraSellableIdToAdd = sizes[0].standardSellableId;

                            // ...but try to find the same size as the order item if possible
                            const groupId: number = sizes[0].sellableSizeGroupId;
                            let sellableSizeId: number = this.menuModel.findSellableSizeId(groupId, this.orderItem.sellable.sellableId);
                            if (sellableSizeId) {
                                let sameSize: SellableSizesData = sizes.find((sellableSizes: SellableSizesData) => sellableSizes.sellableSizeId === sellableSizeId);
                                if (sameSize) {
                                    extraSellableIdToAdd = sameSize.sellableId;
                                }
                            }
                        }

                        let addExtraProduct: MenuExtraProductData = extraGroup.extraProducts.find((extraProduct: MenuExtraProductData) => extraProduct.productLine.sellableId === extraSellableIdToAdd);
                        if (!this.orderItem.isExtraProductAvailable(addExtraProduct, this.menuItem)) {
                            return;
                        }

                        if (addExtraProduct) {

                            // If all the products in the extra are in the size group, select the chosen extra rather than leaving a single option unselected.
                            if (!selectedSizeExtra && sizes.length === extraGroup.extraProducts.length) {
                                this.orderItem.addOrderExtraItem(this.orderItem.createOrderExtraItem(extraGroup, addExtraProduct), false);
                            }

                            selectedExtraProducts.push(addExtraProduct);
                            sellableIdsWithSize.set(addExtraProduct.productLine.sellableId, this.getSizeName(sizes));

                            // Other sizes are excluded (only one shown at once)
                            sizes.forEach((size: SellableSizesData) => {
                                excludedSellableIds.set(size.sellableId, true);
                            });
                        } else {
                            selectedExtraProducts.push(extraProduct);
                        }
                    } else {
                        if (this.menuModel.getStandardItemImage(extraProduct.menuItem)) {
                            this.extraGroupHasImages.set(extraGroup.id, true);
                            this.alignAllSingleSelectExtrasForImages = this.responsiveService.desktop;
                        }
                        selectedExtraProducts.push(extraProduct);
                    }
                } else {
                    if (this.sessionModel.currentApp.showSelectedExtrasFirst) {
                        let selected: boolean = this.preOrderedExtras.find((orderedExtra: OrderExtraItem) => orderedExtra.extraGroup.id === extraGroup.id &&
                            orderedExtra.extraProduct.productLine.sellableId === extraProduct.productLine.sellableId) != undefined;
                        if (selected) {
                            selectedExtraProducts.push(extraProduct);
                        } else {
                            unselectedExtraProducts.push(extraProduct);
                        }
                    } else {
                        selectedExtraProducts.push(extraProduct);
                    }
                }
            });

            const newAvailableExtraProductsForGroup: MenuExtraProductData[] = selectedExtraProducts.concat(unselectedExtraProducts);
            this.availableExtraProducts.set(extraGroup.id, newAvailableExtraProductsForGroup);
            if (this.availableExtraProducts.get(extraGroup.id).length) {
                this.availableExtraGroupCount++;
                if (this.menuModel.extraGroupIsSingleSelect(extraGroup)) {
                    if (!isChildGroup) {
                        extraGroupTier = firstTierExtraGroups;
                    }
                    if (sellableIdsWithSize.size) {
                        this.singleSelectExtrasWithSize.set(extraGroup.id, sellableIdsWithSize);
                    }
                } else if (this.minimalMode && this.menuModel.extraGroupIsOptional(extraGroup)) {
                    if (!isChildGroup) {
                        extraGroupTier = this.combinedExtraGroups;
                    }
                } else {
                    if (!isChildGroup) {
                        extraGroupTier = secondTierExtraGroups;
                    }
                    this.multiSelectExtraGroupIsAlignedForImages.set(extraGroup.id, false);
                    if ((!groupShowsExtraQuantities || this.responsiveService.mobile) && this.availableExtraProducts.get(extraGroup.id).length >= this.sessionModel.currentApp.compactExtrasThreshold) {
                        this.multiSelectExtraGroupIsCompacted.set(extraGroup.id, true);
                        if (this.extraGroupHasImages.get(extraGroup.id)) {
                            anyCompactMultiSelectExtraGroupHasImages = true;
                        }
                    } else if (this.extraGroupHasImages.get(extraGroup.id)) {
                        anyNormalMultiSelectExtraGroupHasImages = true;
                    }
                }
                if (extraGroupTier === undefined) {
                    extraGroupTier = firstTierExtraGroups;
                }
                extraGroupTier.push(extraGroup);
            } else if (isParentGroup) {
                skippingParentGroup = true;
            }

            // Check for conditional extras which have become available due to other extras having been selected, and which may need to be defaulted.
            // Don't default items from single-select groups if there is already an extra item from that group selected.
            // Don't default items from the combined extra groups if we are currently assigning them from the popup.
            if (!(this.assigningCombinedExtras && extraGroupTier === this.combinedExtraGroups)) {
                extraGroup.extraProducts.forEach((extraProduct: MenuExtraProductData) => {
                    if (prevAvailableExtraProducts.size && extraProduct.defaultOrderQty && prevAvailableExtraProductsForGroup.indexOf(extraProduct) < 0 &&
                        !(this.menuModel.extraGroupIsSingleSelect(extraGroup) && this.orderItem.orderedExtras.find((extraItem: OrderExtraItem) => extraItem.extraGroup.id === extraGroup.id) !== undefined)) {
                        additionalDefaultableExtraProducts.push(extraProduct);
                    }
                });
            }

            // Check for any previously available extra products which have become unavailable.
            prevAvailableExtraProductsForGroup.forEach((prevProduct: MenuExtraProductData) => {
                if (newAvailableExtraProductsForGroup.indexOf(prevProduct) < 0 && this.orderItem.removeOrderExtraProduct(extraGroup, prevProduct)) {
                    itemsRemoved = true;
                }
            });
        });

        this.multiSelectExtraGroupIsAlignedForImages.forEach((value, extraId: number) => {
            this.multiSelectExtraGroupIsAlignedForImages.set(extraId,
                !this.alignMultiSelectExtrasBetweenGroups ? this.extraGroupHasImages.get(extraId)
                    : this.multiSelectExtraGroupIsCompacted.get(extraId) ? anyCompactMultiSelectExtraGroupHasImages
                        : anyNormalMultiSelectExtraGroupHasImages);
        });

        this.individualExtraGroups = firstTierExtraGroups.concat(secondTierExtraGroups);

        this.individualExtraGroups.forEach((extraGroup: MenuExtraGroupData) => {
            if (this.menuModel.extraGroupIsSingleSelect(extraGroup)) {
                if (this.extraExpanded.get(extraGroup.id) === undefined) {
                    this.extraExpanded.set(extraGroup.id, true);
                }
            } else if (this.extraExpanded.get(extraGroup.id) === undefined) {
                this.extraExpanded.set(extraGroup.id,
                    this.availableExtraGroupCount === 1 ||
                    !!extraGroup.groupQtyMin ||
                    !this.sessionModel.currentApp.collapseExtras ||
                    this.menuModel.extraGroupHasSingleOption(extraGroup) ||
                    !!this.orderItem.orderedExtras.find((orderExtraItem: OrderExtraItem) => orderExtraItem.extraGroup === extraGroup));
            }
        });

        if (itemsRemoved) {
            this.orderItem.itemChanged(true);
        }

        // If any defaultable extras have become available, add them (if possible) and reload extras since there could be further dependencies.
        // Also reload if items have been removed.
        if (itemsRemoved ||
            (additionalDefaultableExtraProducts.length && this.orderItem.addDefaultExtras(this.compositeItem ? <OrderCompositeItem>this.mainOrderItem : undefined, additionalDefaultableExtraProducts))) {
            this.loadAvailableExtras();
        } else {
            this.changeDetectorRef.detectChanges();
        }
    }

    getSizeName(sizes: SellableSizesData[]): string {
        return sizes[0].externalSizeName || sizes[0].externalName || sizes[0].groupName;
    }

    public extraTitle(extraGroup: MenuExtraGroupData, suffix: string = "title"): string {
        const groupQtyLimit: number = this.menuModel.getExtraGroupEffectiveQtyLimit(extraGroup);
        return this.promotedExtraGroup && extraGroup.promotionMessage && suffix === "title" ? ("'" + extraGroup.promotionMessage + "'")
            : extraGroup.selectionMessage && suffix === "title" ? ("'" + extraGroup.selectionMessage + "'")
                : (this.menuModel.extraGroupHasNoChoice(extraGroup) ? "woa.heading.order.extras.noChoice"
                : this.menuModel.extraGroupIsSingleSelect(extraGroup) ? "woa.heading.order.extras.choose"
                    : this.menuModel.extraGroupHasSingleOption(extraGroup) && this.menuModel.getSingleOptionExtraLimit(extraGroup) === 1 ? "woa.heading.order.extras.${verb}Max1Single"
                        : this.menuModel.extraGroupHasSingleOption(extraGroup) && extraGroup.groupQtyMin && this.menuModel.getSingleOptionExtraLimit(extraGroup) ? "woa.heading.order.extras.chooseMinMaxSingle"
                            : this.menuModel.extraGroupHasSingleOption(extraGroup) && extraGroup.groupQtyMin ? "woa.heading.order.extras.chooseMinSingle"
                                : extraGroup.groupQtyMin && groupQtyLimit ? (extraGroup.groupQtyMin === groupQtyLimit ? "woa.heading.order.extras.${verb}Fixed" : "woa.heading.order.extras.${verb}MinMax")
                                    : extraGroup.groupQtyMin && !groupQtyLimit ? "woa.heading.order.extras.${verb}Min"
                                        : !extraGroup.groupQtyMin && groupQtyLimit ? "woa.heading.order.extras.${verb}Max"
                                            : "woa.heading.order.extras.${verb}")
                .replace("${verb}", this.menuModel.extraGroupHasDefaultOptions(extraGroup) ? "change" : "add") + "." + suffix;
    }

    public extraTitleParams(extraGroup: MenuExtraGroupData): any {
        const count: number = this.orderItem.getExtraGroupCount(extraGroup);
        return {
            "name": extraGroup.externalName || extraGroup.name,
            "count": count ? count : "{{woa.common.none}}",
            "min": extraGroup.groupQtyMin,
            "max": this.menuModel.extraGroupHasSingleOption(extraGroup) ? this.menuModel.getSingleOptionExtraLimit(extraGroup) : this.menuModel.getExtraGroupEffectiveQtyLimit(extraGroup)
        };
    }

    selectSingleSelectExtra(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, index: number, option?: OrderItemOptionControlComponent, body?: HTMLElement): boolean {
        if (this.orderItem.getOrderedExtraByExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId) != undefined) {
            if (this.getSwapLink(extraGroup, extraProduct)) {
                // noinspection JSIgnoredPromiseFromCall
                this.swapExtra(extraGroup, extraProduct, index, event);
            }
            return false;
        }

        if (!this.canSingleSelectExtra(extraGroup, extraProduct)) {
            if (this.menuModel.itemOutOfStock(extraProduct.menuItem)) {
                // noinspection JSIgnoredPromiseFromCall
                this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: extraProduct.menuItem.name});
            } else if (this.extraProductAtLimit(extraProduct)) {
                // noinspection JSIgnoredPromiseFromCall
                this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: extraProduct.menuItem.name});
            }
            return false;
        }

        this.orderItem.orderedExtras.find((orderedExtra: OrderExtraItem) => {
            if (extraGroup.id === orderedExtra.extraGroup.id) {
                this.orderItem.removeOrderExtraItem(orderedExtra.identifier, false);
                return true;
            }
            return false;
        });
        this.orderItem.addOrderExtraItem(this.orderItem.createOrderExtraItem(extraGroup, extraProduct), true);

        this.onChange.emit(extraGroup);
        if (body) {
            this.setExtraBodyHeight(extraGroup, body);
        }
        this.changeDetectorRef.detectChanges();

        return true;
    }

    selectMultiSelectExtra(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, body: HTMLElement): boolean {
        if (this.showMultiSelectExtraUnavailable(extraGroup, extraProduct)) {
            if (this.menuModel.itemOutOfStock(extraProduct.menuItem)) {
                // noinspection JSIgnoredPromiseFromCall
                this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: extraProduct.menuItem.name});
            } else if (this.extraProductAtLimit(extraProduct)) {
                // noinspection JSIgnoredPromiseFromCall
                this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: extraProduct.menuItem.name});
            }
            return false;
        }

        let orderExtraItem: OrderExtraItem = this.orderItem.getOrderedExtraByExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId);
        if (orderExtraItem) {
            if (this.menuModel.extraGroupHasSingleOption(extraGroup) && extraGroup.groupQtyMin) {
                return false;
            }
            this.orderItem.removeOrderExtraItem(orderExtraItem.identifier, true);
        } else {
            if (this.orderItem.hasExtraGroupReachedLimit(extraGroup)) {
                // noinspection JSIgnoredPromiseFromCall
                this.notificationService.displayAlert(null, "woa.message.order.qtyLimitReached", {item: extraGroup.externalName || extraGroup.name});
                return false;
            }
            this.orderItem.addOrderExtraItem(this.orderItem.createOrderExtraItem(extraGroup, extraProduct), true);
        }
        this.onChange.next(extraGroup);
        this.setExtraBodyHeight(extraGroup, body);
        this.changeDetectorRef.detectChanges();

        return true;
    }

    getExtraQuantity(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): number {
        const orderExtraItem: OrderExtraItem = this.orderItem.getOrderedExtraByExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId);
        return orderExtraItem ? orderExtraItem.quantity : 0;
    }

    /**
     * @param extraGroup
     * @param extraProduct
     * @param showForOrderedOnly If true, extra quantities are only to be shown for a product which has been ordered
     */
    showExtraQuantity(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, showForOrderedOnly: boolean = false): boolean {
        const qty: number = this.getExtraQuantity(extraGroup, extraProduct);
        const extraLimit: number = this.menuModel.getExtraLimit(extraProduct);
        const groupQtyLimit: number = this.menuModel.getExtraGroupEffectiveQtyLimit(extraGroup);
        return qty > 1 || ((groupQtyLimit == null || groupQtyLimit > 1) && (extraLimit === 0 || extraLimit > 1) && (!showForOrderedOnly || qty > 0));
    }

    showMultiSelectExtraUnavailable(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean {
        return this.getExtraQuantity(extraGroup, extraProduct) == 0 && !this.canIncrementExtra(extraGroup, extraProduct);
    }

    showSingleSelectExtraUnavailable(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean {
        return this.getExtraQuantity(extraGroup, extraProduct) == 0 && !this.canSingleSelectExtra(extraGroup, extraProduct);
    }

    showExtrasSelected(extraGroup: MenuExtraGroupData): boolean {
        const groupQtyLimit: number = this.menuModel.getExtraGroupEffectiveQtyLimit(extraGroup);
        return !this.menuModel.extraGroupIsSingleSelect(extraGroup) && (extraGroup.groupQtyMin || groupQtyLimit) && !(this.menuModel.extraGroupHasSingleOption(extraGroup) && groupQtyLimit === 1);
    }

    showMultiSelectCheckbox(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean {
        return !this.menuModel.extraGroupHasSingleOption(extraGroup) || !extraGroup.groupQtyMin || this.menuModel.getExtraLimit(extraProduct) === 1;
    }

    disableMultiSelectCheckbox(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean {
        return this.menuModel.extraGroupHasSingleOption(extraGroup) && extraGroup.groupQtyMin && this.getExtraQuantity(extraGroup, extraProduct) > 0;
    }

    extraProductAtLimit(extraProduct: MenuExtraProductData): boolean {
        if (!this.mainOrderItem) {
            return false;
        }
        const extraQty: number = this.orderItem.sellableQtyOrdered(this.orderItem.quantity, extraProduct.productLine.sellableId);

        // On mobile, a popup is used to edit optional multi-select extras. In this case, we are not working directly on the order item,
        // so removing a product doesn't exclude it from the count, which can lead to the product not being able to be re-added.
        // For this case, we keep track of the extra product quantities on entry, and always allow them to be at least their original value.
        if (this.allowInitialState && (extraQty < (this.initialQtyOrderedBySellableId[extraProduct.productLine.sellableId] || 0))) {
            return false;
        }
        return this.orderModel.sellableAtLimit(extraProduct.menuItem ? extraProduct.menuItem.sellable.sellableId : extraProduct.productLine.sellableId, this.mainOrderItem.quantity, extraQty, this.originalOrderItem);
    }

    canDecrementExtra(extraGroup: MenuExtraGroupData): boolean {
        return this.menuModel.extraGroupHasSingleOption(extraGroup) ? !this.orderItem.hasExtraGroupReachedMin(extraGroup) : true;
    }

    canIncrementExtra(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean {
        if (this.orderItem.hasExtraGroupReachedLimit(extraGroup)) {
            return false;
        }
        if (this.extraProductAtLimit(extraProduct)) {
            return false;
        }
        let orderExtraItem: OrderExtraItem = this.orderItem.getOrderedExtraByExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId);
        if (orderExtraItem) {
            let extraLimit: number = this.menuModel.getExtraLimit(extraProduct);
            return extraLimit == 0 || orderExtraItem.quantity < extraLimit;
        }
        return true;
    }

    canSingleSelectExtra(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean {
        return !this.extraProductAtLimit(extraProduct);
    }

    isExpanded(extraGroup: MenuExtraGroupData): boolean {
        return this.extraExpanded.get(extraGroup.id);
    }

    showImage(extraGroup: MenuExtraGroupData, extraProduct?: MenuExtraProductData): boolean {
        if (this.extraGroupHasImages.get(extraGroup.id)) {
            return true;
        }
        if (!extraProduct) {
            extraProduct = extraGroup.extraProducts.find((extraProduct: MenuExtraProductData) => this.orderItem.getOrderedExtraByExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId) != undefined);
        }
        return (extraProduct && this.menuModel.getStandardItemImage(extraProduct.menuItem) && !!this.getSwapLink(extraGroup, extraProduct));
    }

    anyOptionHasPrice(extraGroupId: number): boolean {
        return !!this.availableExtraProducts.get(extraGroupId).find((extraProduct: MenuExtraProductData) => !!extraProduct.price);
    }

    getSwapLink(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): string {
        return (this.orderItem.getOrderedExtraByExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId) != undefined && this.singleSelectExtrasWithSize.has(extraGroup.id))
            ? this.singleSelectExtrasWithSize.get(extraGroup.id).get(extraProduct.productLine.sellableId) : null;
    }

    toggleExtra(extraGroup: MenuExtraGroupData, body: HTMLElement, group: HTMLElement): void {
        if (this.promotedExtraGroup) {
            return;
        }
        this.extraExpanded.set(extraGroup.id, !this.extraExpanded.get(extraGroup.id));
        this.setExtraBodyHeight(extraGroup, body, group);
    }

    // noinspection JSUnusedLocalSymbols
    setExtraBodyHeight(extraGroup: MenuExtraGroupData, body: HTMLElement, group?: HTMLElement): void {
        body.style.maxHeight = (body.scrollHeight + 1) + "px";
        setTimeout(() => {
            const expanded: boolean = this.extraExpanded.get(extraGroup.id);
            body.style.maxHeight = expanded ? (body.scrollHeight + 1) + "px" : "0";
            /*
            if (expanded && group) {
                setTimeout(() => group.scrollIntoView({
                    block: "center",
                    inline: "nearest",
                    behavior: "smooth"
                }), 330);
            }
            */
        });
    }

    decrementExtra(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, body: HTMLElement): void {
        let orderExtraItem: OrderExtraItem = this.orderItem.getOrderedExtraByExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId);
        if (orderExtraItem) {
            orderExtraItem.decrementQuantity();
            if (orderExtraItem.quantity === 0) {
                this.orderItem.removeOrderExtraItem(orderExtraItem.identifier, true);
            }
        }
        this.onChange.next(extraGroup);
        this.setExtraBodyHeight(extraGroup, body);
        this.changeDetectorRef.detectChanges();
    }

    incrementExtra(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, body: HTMLElement): void {
        let orderExtraItem: OrderExtraItem = this.orderItem.getOrderedExtraByExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId);
        if (!orderExtraItem) {
            this.selectMultiSelectExtra(extraGroup, extraProduct, body);
        } else {
            if (this.orderItem.hasExtraGroupReachedLimit(extraGroup)) {
                // noinspection JSIgnoredPromiseFromCall
                this.notificationService.displayAlert(null, "woa.message.order.qtyLimitReached", {item: extraGroup.externalName || extraGroup.name});
                return;
            }
            if (this.menuModel.itemOutOfStock(extraProduct.menuItem)) {
                // noinspection JSIgnoredPromiseFromCall
                this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: extraProduct.menuItem.name});
                return;
            } else if (this.extraProductAtLimit(extraProduct)) {
                // noinspection JSIgnoredPromiseFromCall
                this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: extraProduct.menuItem.name});
                return;
            }
            if (this.canIncrementExtra(extraGroup, extraProduct)) {
                let orderExtraItem: OrderExtraItem = this.orderItem.getOrderedExtraByExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId);
                orderExtraItem.incrementQuantity();
            }
            this.onChange.next(extraGroup);
            this.setExtraBodyHeight(extraGroup, body);
            this.changeDetectorRef.detectChanges();
        }
    }

    async swapExtra(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, index: number, event?: Event): Promise<void> {
        event && event.stopPropagation();

        let sizes: SellableSizesData[] = this.menuModel.findExtraSizes(extraProduct.productLine.sellableId, extraGroup);
        let extraProductsToSelect: MenuExtraProductData[] = sizes
            .map((size) => extraGroup.extraProducts.find((extraProduct: MenuExtraProductData) => extraProduct.productLine.sellableId === size.sellableId))
            .filter((extraProduct: MenuExtraProductData) => {
                if (extraProduct.menuItem) {
                    return (!(extraProduct.menuItem.hidden ||
                        (this.orderingContextModel.currentStore.orderAheadStore && !this.menuModel.extraItemIsAvailableForOrderType(extraProduct.menuItem, OrderType.ORDER_AHEAD)) ||
                        (this.orderingContextModel.currentStore.orderAtTableStore && !this.menuModel.extraItemIsAvailableForOrderType(extraProduct.menuItem, OrderType.ORDER_AT_TABLE)) ||
                        !this.menuModel.extraItemIsAvailableForFilters(extraProduct.menuItem, this.menuItem, this.orderItem.selectedFilterTags)));
                }
                return !this.menuItem.filterExtras;
            });

        const selectedExtra: MenuExtraProductData = await this.navigationService.createPage("woa-order-item-extra-sizes-edit-page", {
            orderItem: this.orderItem,
            extra: extraGroup,
            availableExtraProducts: extraProductsToSelect,
            selectedExtraProduct: extraProduct,
            groupName: this.getSizeName(sizes)
        });
        if (selectedExtra && selectedExtra.productLine.sellableId !== extraProduct.productLine.sellableId) {
            if (this.selectSingleSelectExtra(extraGroup, selectedExtra, index)) {
                this.availableExtraProducts.get(extraGroup.id)[index] = selectedExtra;
                this.singleSelectExtrasWithSize.get(extraGroup.id).set(selectedExtra.productLine.sellableId, this.getSizeName(sizes));
            }
        }
    }

    async chooseCombinedExtras(event: MouseEvent): Promise<void> {
        if (event) {
            event.stopPropagation();
        }
        let editOrderItem: OrderItem = this.orderItem.createClone();
        if (await this.navigationService.createPage("woa-order-item-combined-extras-edit-page", {
            orderItem: editOrderItem,
            mainOrderItem: this.mainOrderItem,
            originalOrderItem: this.originalOrderItem,
            menuItem: this.menuItem
        })) {
            this.assigningCombinedExtras = true;
            this.orderItem.assignExtras(editOrderItem, undefined, true);
            this.combinedExtraGroups.forEach((extraGroup: MenuExtraGroupData) => this.onChange.next(extraGroup));
            this.changeDetectorRef.detectChanges();
            this.assigningCombinedExtras = false;
        }
    }

    trackById(index, extra: MenuExtraProductData) {
        return extra.productLine.sellableId;
    }
}



// WEBPACK FOOTER //
// ./src/app/order/order-item/order-item-options-panel/order-item-extras-edit-panel/order-item-extras-edit-panel.component.ts